/**
 * Object hooks — delimited listing, version listing, presigned upload/download,
 * delete and folder creation. Uploads/downloads go DIRECT browser↔S3 via a
 * short-lived presigned URL the backend mints; the bytes never transit @mgmt.
 *
 * react-query keys include `bucket`, `prefix` and the pagination `marker` so
 * each folder page caches independently.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  serviceApiV3,
  unwrap,
  storageBase,
  s3Keys,
  type ObjectListing,
  type ObjectVersion,
  type PresignUpload,
} from './_shared';

const EMPTY_LISTING: ObjectListing = {
  common_prefixes: [],
  contents: [],
  is_truncated: false,
  next_token: null,
};

/** One page of objects/folders under `prefix` (delimiter `/`). */
export function useObjects(
  serviceId: string | undefined,
  bucket: string | undefined,
  prefix: string,
  marker = '',
) {
  return useQuery<ObjectListing>({
    queryKey: s3Keys.objects(serviceId ?? '', bucket ?? '', prefix, marker),
    enabled: !!serviceId && !!bucket,
    staleTime: 10_000,
    queryFn: async () => {
      const res = await serviceApiV3.get(
        `${storageBase(serviceId!)}/buckets/${bucket}/objects`,
        { params: { prefix, marker: marker || undefined } },
      );
      const data = unwrap<ObjectListing>(res);
      return {
        ...EMPTY_LISTING,
        ...data,
        common_prefixes: data?.common_prefixes ?? [],
        contents: data?.contents ?? [],
      };
    },
  });
}

/** Version history for objects under `prefix`. */
export function useObjectVersions(
  serviceId: string | undefined,
  bucket: string | undefined,
  prefix: string,
  enabled = true,
) {
  return useQuery<ObjectVersion[]>({
    queryKey: s3Keys.versions(serviceId ?? '', bucket ?? '', prefix),
    enabled: !!serviceId && !!bucket && enabled,
    queryFn: async () => {
      const res = await serviceApiV3.get(
        `${storageBase(serviceId!)}/buckets/${bucket}/objects/versions`,
        { params: { prefix } },
      );
      const data = unwrap<{ versions?: ObjectVersion[] }>(res);
      return data?.versions ?? [];
    },
  });
}

/** Mint a presigned PUT URL for a direct browser→S3 upload. */
export function usePresignUpload(serviceId: string, bucket: string) {
  return useMutation<PresignUpload, Error, { key: string; content_type: string }>({
    mutationFn: async (input) =>
      unwrap<PresignUpload>(
        await serviceApiV3.post(
          `${storageBase(serviceId)}/buckets/${bucket}/presign-upload`,
          input,
        ),
      ),
  });
}

/** Resolve a presigned GET URL for a direct browser download. */
export function usePresignDownload(serviceId: string, bucket: string) {
  return useMutation<string, Error, string>({
    mutationFn: async (key) => {
      const res = await serviceApiV3.get(
        `${storageBase(serviceId)}/buckets/${bucket}/presign-download`,
        { params: { key } },
      );
      return unwrap<{ url: string }>(res).url;
    },
  });
}

export function useDeleteObject(serviceId: string, bucket: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (key) => {
      await serviceApiV3.delete(
        `${storageBase(serviceId)}/buckets/${bucket}/objects`,
        { params: { key } },
      );
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['s3', serviceId, 'bucket', bucket, 'objects'] }),
  });
}

/** Create a zero-byte folder marker at `prefix` (must end with `/`). */
export function useCreateFolder(serviceId: string, bucket: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (prefix) => {
      await serviceApiV3.post(
        `${storageBase(serviceId)}/buckets/${bucket}/folder`,
        { prefix },
      );
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['s3', serviceId, 'bucket', bucket, 'objects'] }),
  });
}
